import { cva } from 'class-variance-authority'
import type { HTMLAttributes, ReactNode } from 'react'
import { Button as AriaButton, Link as AriaLink } from 'react-aria-components'
import { cn } from '../../lib/cn'
import { focusRing } from '../../lib/focus-ring'
import { Avatar, type AvatarTintBySize } from '../../atoms/avatar/avatar'
import { Icon, type IconName } from '../../atoms/icon/icon'

/**
 * The Horizontal Cards set (463:54126), Property 1 =
 *
 * - Dropdown designer (445:44853): `designer`, a person with a trailing chevron.
 * - DP Link (445:44854): `dp-link`, a copyable code with an external action link.
 * - Uploaded File (403:43844): `uploaded-file`, a file name and size with a download button.
 * - Component 352 (463:54125): `copy-url`, a labelled value that wraps, with a copy button.
 *   The Figma left it with its default name; it is the SharePoint delivery folder card.
 *
 * All four share the white card with a 1px Stroke and the 16px radius; the stroke
 * sits inside the frame, so it is an inset ring. The designer name and the file
 * name bind the Components board's second "Dark" (#080712), `dark-ink`; the designer
 * avatar is the 36.125px disc with normal-width initials (Avatar `size="card"`).
 *
 * Long text wraps rather than truncating, as the Figma's full-width text does: the
 * designer's avatar and text sit in a fixed 252.75px column (445:44790), and the
 * file name fills the row beside its icon. The designer chevron is decorative: the
 * Figma names the variant "Dropdown designer" but draws no menu, so it opens none.
 *
 * Keyboard focus takes the kit's accent outline, which is not drawn in the Figma.
 */
export const horizontalCardVariants = cva(
	'flex items-center rounded-atlas-lg bg-atlas-light p-4 font-atlas-sans inset-ring inset-ring-atlas-stroke',
	{
		variants: {
			variant: {
				designer: 'justify-between',
				'dp-link': 'justify-between',
				'uploaded-file': 'gap-4 pr-6',
				'copy-url': 'justify-center',
			},
		},
		defaultVariants: { variant: 'designer' },
	},
)

const iconActionClass = cn('inline-flex shrink-0 cursor-pointer rounded-atlas-sm', focusRing)

function IconAction({ icon, label, onPress }: { icon: IconName; label: string; onPress?: () => void }) {
	return (
		<AriaButton aria-label={label} onPress={onPress} className={iconActionClass}>
			<Icon name={icon} />
		</AriaButton>
	)
}

type CardBase = Omit<HTMLAttributes<HTMLDivElement>, 'children'>

/** A person, e.g. the designer assigned to a work order. */
interface DesignerCardProps extends CardBase {
	variant?: 'designer'
	name: string
	/** The person's role, e.g. "UX/UI Designer". */
	role: string
	initials: string
	tint?: AvatarTintBySize['card']
}

/** A Veeva Distribution Package: a copyable code and a link that opens it in Veeva. */
interface DpLinkCardProps extends CardBase {
	variant: 'dp-link'
	/** Caption above the value, e.g. "Veeva Distribution Package code". */
	label: string
	value: string
	onCopyValue?: (value: string) => void
	copyLabel?: string
	/** The external link's text, e.g. "Open in Veeva". */
	actionLabel: string
	actionHref: string
}

/** An uploaded file with its size and a download button. */
interface UploadedFileCardProps extends CardBase {
	variant: 'uploaded-file'
	fileName: string
	/** The formatted size, e.g. "2.4 MB". Formatting is the caller's. */
	fileSize: string
	onDownload?: () => void
	downloadLabel?: string
}

/** A labelled value, such as a URL, that wraps over lines and can be copied. */
interface CopyUrlCardProps extends CardBase {
	variant: 'copy-url'
	label: string
	value: string
	onCopyValue?: (value: string) => void
	copyLabel?: string
}

export type HorizontalCardProps = DesignerCardProps | DpLinkCardProps | UploadedFileCardProps | CopyUrlCardProps

function DesignerContent({
	name,
	role,
	initials,
	tint,
}: Pick<DesignerCardProps, 'name' | 'role' | 'initials' | 'tint'>) {
	return (
		<>
			<div className="flex w-atlas-designer-column shrink-0 items-center gap-2.5">
				<Avatar size="card" initials={initials} tint={tint} aria-hidden="true" />
				<div className="flex min-w-0 flex-1 flex-col break-words">
					<span className="w-full text-atlas-body-semibold text-atlas-dark-ink">{name}</span>
					<span className="w-full text-atlas-label text-atlas-light-dark">{role}</span>
				</div>
			</div>
			<Icon name="chevron-down" />
		</>
	)
}

function DpLinkContent(props: Omit<DpLinkCardProps, keyof CardBase | 'variant'>) {
	const { label, value, onCopyValue, copyLabel = 'Copy', actionLabel, actionHref } = props
	return (
		<>
			<div className="flex min-w-0 flex-col items-start gap-0.5">
				<span className="font-atlas-poppins text-atlas-dp-label text-atlas-light-dark">{label}</span>
				<div className="flex items-start gap-2">
					<span className="text-atlas-link-value text-atlas-dark">{value}</span>
					<IconAction icon="copy-01" label={copyLabel} onPress={() => onCopyValue?.(value)} />
				</div>
			</div>
			<AriaLink
				href={actionHref}
				target="_blank"
				rel="noopener noreferrer"
				className={cn(
					'inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-atlas-sm text-atlas-link-action text-atlas-accent',
					focusRing,
				)}
			>
				{actionLabel}
				<Icon name="share-04" />
			</AriaLink>
		</>
	)
}

function UploadedFileContent(props: Omit<UploadedFileCardProps, keyof CardBase | 'variant'>) {
	const { fileName, fileSize, onDownload, downloadLabel = `Download ${props.fileName}` } = props
	return (
		<>
			<div className="flex min-w-0 flex-1 items-center gap-2.5">
				<Icon name="image-01" />
				<div className="flex min-w-0 flex-1 flex-col break-words">
					<span className="w-full text-atlas-body-semibold text-atlas-dark-ink">{fileName}</span>
					<span className="w-full text-atlas-label text-atlas-light-dark">{fileSize}</span>
				</div>
			</div>
			<IconAction icon="download-01" label={downloadLabel} onPress={onDownload} />
		</>
	)
}

function CopyUrlContent({
	label,
	value,
	onCopyValue,
	copyLabel = 'Copy',
}: Omit<CopyUrlCardProps, keyof CardBase | 'variant'>) {
	return (
		<div className="flex min-w-0 flex-1 flex-col items-start gap-1">
			<span className="w-full text-atlas-label text-atlas-light-dark">{label}</span>
			<div className="flex w-full items-center gap-3">
				<span className="min-w-0 flex-1 break-words text-atlas-body text-atlas-dark">{value}</span>
				<IconAction icon="copy-01" label={copyLabel} onPress={() => onCopyValue?.(value)} />
			</div>
		</div>
	)
}

function renderContent(props: HorizontalCardProps): { rest: CardBase; content: ReactNode } {
	switch (props.variant) {
		case 'dp-link': {
			const { variant, label, value, onCopyValue, copyLabel, actionLabel, actionHref, ...rest } = props
			const content = <DpLinkContent {...{ label, value, onCopyValue, copyLabel, actionLabel, actionHref }} />
			return { rest, content }
		}
		case 'uploaded-file': {
			const { variant, fileName, fileSize, onDownload, downloadLabel, ...rest } = props
			return { rest, content: <UploadedFileContent {...{ fileName, fileSize, onDownload, downloadLabel }} /> }
		}
		case 'copy-url': {
			const { variant, label, value, onCopyValue, copyLabel, ...rest } = props
			return { rest, content: <CopyUrlContent {...{ label, value, onCopyValue, copyLabel }} /> }
		}
		default: {
			const { variant, name, role, initials, tint, ...rest } = props
			return { rest, content: <DesignerContent {...{ name, role, initials, tint }} /> }
		}
	}
}

/**
 * A full-width white card holding one record on a work order: the assigned
 * designer, a distribution package, an uploaded file or a delivery folder URL.
 * Width is the caller's; the board draws 708px. Copy and download are callbacks:
 * the screen writes the clipboard or fetches the file and reports the outcome
 * through the app's notifications.
 */
export function HorizontalCard(props: HorizontalCardProps) {
	const { rest, content } = renderContent(props)
	const { className, ...domProps } = rest
	return (
		<div {...domProps} className={cn(horizontalCardVariants({ variant: props.variant ?? 'designer' }), className)}>
			{content}
		</div>
	)
}
